import { supabase } from '../lib/supabase';

// ============================================
// DEMO USER SUPPORT
// ============================================

// Module-level demo user — set by AuthContext when demo mode is active
let _demoUser = null;

export function setDemoUser(user) {
  _demoUser = user;
}

export function clearDemoUser() {
  _demoUser = null;
}

/**
 * Returns the currently authenticated user.
 * If a demo user is active (no real Supabase session), returns the demo user.
 * Otherwise fetches the real user from Supabase auth.
 */
async function getAuthUser() {
  if (_demoUser) return _demoUser;
  const { data: { user } } = await supabase?.auth?.getUser();
  return user;
}

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
}

// ============================================
// PILGRIMS
// ============================================

export async function getPilgrims() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('pilgrims')?.select(`
        *,
        group:groups(name)
      `)?.eq('agency_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || [])?.map(pilgrim => ({
      ...pilgrim,
      group: pilgrim?.group?.name || null
    }));
  } catch (error) {
    console.error('Get pilgrims error:', error);
    throw error;
  }
}

export async function getPilgrimById(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('pilgrims')?.select(`
        *,
        group:groups(name, type, departure_date, return_date)
      `)?.eq('id', id)?.eq('agency_id', user?.id)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get pilgrim error:', error);
    throw error;
  }
}

export async function createPilgrim(pilgrimData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...pilgrimData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('pilgrims')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create pilgrim error:', error);
    throw error;
  }
}

export async function updatePilgrim(id, pilgrimData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(pilgrimData);

    const { data, error } = await supabase?.from('pilgrims')?.update(snakeData)?.eq('id', id)?.eq('agency_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update pilgrim error:', error);
    throw error;
  }
}

export async function deletePilgrim(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('pilgrims')?.delete()?.eq('id', id)?.eq('agency_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Delete pilgrim error:', error);
    throw error;
  }
}

// ============================================
// GROUPS
// ============================================

export async function getGroups() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('groups')?.select('*')?.eq('agency_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get groups error:', error);
    throw error;
  }
}

export async function getGroupById(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('groups')?.select('*')?.eq('id', id)?.eq('agency_id', user?.id)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get group error:', error);
    throw error;
  }
}

export async function createGroup(groupData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...groupData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('groups')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create group error:', error);
    throw error;
  }
}

export async function updateGroup(id, groupData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(groupData);

    const { data, error } = await supabase?.from('groups')?.update(snakeData)?.eq('id', id)?.eq('agency_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update group error:', error);
    throw error;
  }
}

export async function deleteGroup(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('groups')?.delete()?.eq('id', id)?.eq('agency_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Delete group error:', error);
    throw error;
  }
}

// ============================================
// VISA APPLICATIONS
// ============================================

export async function getVisaApplications() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('visa_applications')?.select('*')?.eq('agency_id', user?.id)?.order('application_date', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get visa applications error:', error);
    throw error;
  }
}

export async function createVisaApplication(visaData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...visaData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('visa_applications')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create visa application error:', error);
    throw error;
  }
}

export async function updateVisaApplication(id, visaData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(visaData);

    const { data, error } = await supabase?.from('visa_applications')?.update(snakeData)?.eq('id', id)?.eq('agency_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update visa application error:', error);
    throw error;
  }
}

// ============================================
// TRANSACTIONS
// ============================================

export async function getTransactions() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('transactions')?.select(`
        *,
        group:groups(name)
      `)?.eq('agency_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || [])?.map(transaction => ({
      ...transaction,
      group: transaction?.group?.name || null
    }));
  } catch (error) {
    console.error('Get transactions error:', error);
    throw error;
  }
}

export async function createTransaction(transactionData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...transactionData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('transactions')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create transaction error:', error);
    throw error;
  }
}

export async function updateTransaction(id, transactionData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(transactionData);

    const { data, error } = await supabase?.from('transactions')?.update(snakeData)?.eq('id', id)?.eq('agency_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update transaction error:', error);
    throw error;
  }
}

// ============================================
// NOTIFICATIONS
// ============================================

export async function getNotifications() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('timestamp', { ascending: false })?.limit(20);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get notifications error:', error);
    throw error;
  }
}

export async function markNotificationAsRead(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('notifications')?.update({ read: true })?.eq('id', id)?.eq('user_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Mark notification as read error:', error);
    throw error;
  }
}

// ============================================
// USER PROFILE
// ============================================

export async function getUserProfile() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get user profile error:', error);
    throw error;
  }
}

export async function updateUserProfile(profileData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(profileData);

    const { data, error } = await supabase?.from('user_profiles')?.update(snakeData)?.eq('id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update user profile error:', error);
    throw error;
  }
}

// ============================================
// DASHBOARD STATISTICS
// ============================================

export async function getDashboardStats() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const [pilgrims, groups, visas, transactions] = await Promise.all([
      getPilgrims(),
      getGroups(),
      getVisaApplications(),
      getTransactions()
    ]);

    const activePilgrims = pilgrims?.filter(p => p?.paymentStatus !== 'refunded')?.length || 0;
    const pendingVisas = visas?.filter(v => v?.status === 'pending' || v?.status === 'in_review')?.length || 0;
    const activeGroups = groups?.filter(g => g?.status === 'confirmed' || g?.status === 'in_progress')?.length || 0;
    const totalRevenue = transactions?.filter(t => t?.status === 'completed')?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0) || 0;

    return {
      activePilgrims,
      pendingVisas,
      activeGroups,
      totalRevenue,
      totalPilgrims: pilgrims?.length || 0,
      totalGroups: groups?.length || 0,
      totalTransactions: transactions?.length || 0
    };
  } catch (error) {
    console.error('Get dashboard stats error:', error);
    throw error;
  }
}

// ============================================
// ANALYTICS
// ============================================

export async function getAnalyticsData(startDate, endDate) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch all required data
    const [pilgrims, groups, visaApplications, transactions] = await Promise.all([
      supabase?.from('pilgrims')?.select('*')?.eq('agency_id', user?.id),
      supabase?.from('groups')?.select('*')?.eq('agency_id', user?.id),
      supabase?.from('visa_applications')?.select('*')?.eq('agency_id', user?.id),
      supabase?.from('transactions')?.select('*')?.eq('agency_id', user?.id)
    ]);

    // Handle errors
    if (pilgrims?.error || groups?.error || visaApplications?.error || transactions?.error) {
      console.error('Error fetching analytics data');
      return getDefaultAnalyticsData();
    }

    const pilgrimsData = pilgrims?.data || [];
    const groupsData = groups?.data || [];
    const visasData = visaApplications?.data || [];
    const transactionsData = transactions?.data || [];

    // Calculate KPIs
    const totalPilgrims = pilgrimsData?.length;
    const completedPayments = pilgrimsData?.filter(p => p?.payment_status === 'paid')?.length;
    const conversionRate = totalPilgrims > 0 ? ((completedPayments / totalPilgrims) * 100)?.toFixed(1) : 0;
    
    const totalRevenue = transactionsData
      ?.filter(t => t?.status === 'completed')
      ?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
    const avgRevenuePerPilgrim = totalPilgrims > 0 ? Math.round(totalRevenue / totalPilgrims) : 0;
    
    const completedDocuments = pilgrimsData?.filter(p => p?.document_status === 'complete')?.length;
    const documentCompletionRate = totalPilgrims > 0 ? ((completedDocuments / totalPilgrims) * 100)?.toFixed(1) : 0;
    
    // Seasonal performance (Omra vs Hajj)
    const omraGroups = groupsData?.filter(g => g?.type === 'Omra')?.length;
    const hajjGroups = groupsData?.filter(g => g?.type === 'Hajj')?.length;
    const seasonalPerformance = omraGroups > hajjGroups ? 'Omra' : hajjGroups > omraGroups ? 'Hajj' : 'Balanced';

    // Revenue trend (last 6 months)
    const revenueTrend = generateRevenueTrend(transactionsData, pilgrimsData);

    // Conversion funnel data
    const conversionData = [
      { stage: 'Registered', rate: 100 },
      { stage: 'Documents Submitted', rate: documentCompletionRate },
      { stage: 'Visa Approved', rate: totalPilgrims > 0 ? ((visasData?.filter(v => v?.status === 'approved')?.length / totalPilgrims) * 100)?.toFixed(1) : 0 },
      { stage: 'Payment Completed', rate: conversionRate }
    ];

    // Performance comparison (current vs previous period)
    const performanceComparison = generatePerformanceComparison(groupsData, transactionsData);

    return {
      conversionRate,
      conversionRateChange: '+5.2%', // Mock change
      avgRevenuePerPilgrim,
      avgRevenueChange: '+8.1%', // Mock change
      documentCompletionRate,
      documentCompletionChange: '+3.5%', // Mock change
      seasonalPerformance,
      seasonalChange: '+12%', // Mock change
      revenueTrend,
      conversionData,
      performanceComparison
    };
  } catch (error) {
    console.error('Get analytics data error:', error);
    return getDefaultAnalyticsData();
  }
}

function generateRevenueTrend(transactions, pilgrims) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const currentMonth = new Date()?.getMonth();
  
  return months?.map((month, index) => {
    const monthIndex = (currentMonth - 5 + index + 12) % 12;
    const revenue = Math.random() * 500000 + 200000; // Mock data
    const pilgrimCount = Math.floor(Math.random() * 50 + 20); // Mock data
    
    return {
      month,
      revenue: Math.round(revenue),
      pilgrims: pilgrimCount
    };
  });
}

function generatePerformanceComparison(groups, transactions) {
  return [
    {
      category: 'Omra Groups',
      current: groups?.filter(g => g?.type === 'Omra')?.length || 0,
      previous: Math.max(0, (groups?.filter(g => g?.type === 'Omra')?.length || 0) - 2)
    },
    {
      category: 'Hajj Groups',
      current: groups?.filter(g => g?.type === 'Hajj')?.length || 0,
      previous: Math.max(0, (groups?.filter(g => g?.type === 'Hajj')?.length || 0) - 1)
    },
    {
      category: 'Total Revenue',
      current: Math.round(transactions?.filter(t => t?.status === 'completed')?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0) / 1000),
      previous: Math.round(transactions?.filter(t => t?.status === 'completed')?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0) / 1000 * 0.85)
    },
    {
      category: 'Visa Approvals',
      current: Math.floor(Math.random() * 50 + 30),
      previous: Math.floor(Math.random() * 40 + 20)
    }
  ];
}

function getDefaultAnalyticsData() {
  return {
    conversionRate: 0,
    conversionRateChange: '0%',
    avgRevenuePerPilgrim: 0,
    avgRevenueChange: '0%',
    documentCompletionRate: 0,
    documentCompletionChange: '0%',
    seasonalPerformance: 'N/A',
    seasonalChange: '0%',
    revenueTrend: [],
    conversionData: [],
    performanceComparison: []
  };
}

// ============================================
// ROI ANALYSIS
// ============================================

export async function getROIAnalysisData() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    // Fetch groups with revenue and costs
    const { data: groups, error: groupsError } = await supabase
      ?.from('groups')
      ?.select('*')
      ?.eq('agency_id', user?.id)
      ?.order('created_at', { ascending: false });

    if (groupsError) {
      if (isSchemaError(groupsError)) {
        console.error('Schema error:', groupsError?.message);
        throw groupsError;
      }
      return null;
    }

    // Fetch cost entries for detailed analysis
    const { data: costEntries, error: costError } = await supabase
      ?.from('cost_entries')
      ?.select('*')
      ?.eq('agency_id', user?.id)
      ?.eq('approval_status', 'approved');

    if (costError && !isSchemaError(costError)) {
      console.log('Cost entries error:', costError?.message);
    }

    // Fetch transactions for revenue analysis
    const { data: transactions, error: transError } = await supabase
      ?.from('transactions')
      ?.select('*')
      ?.eq('agency_id', user?.id)
      ?.eq('status', 'completed');

    if (transError && !isSchemaError(transError)) {
      console.log('Transactions error:', transError?.message);
    }

    return {
      groups: toCamelCase(groups || []),
      costEntries: toCamelCase(costEntries || []),
      transactions: toCamelCase(transactions || [])
    };
  } catch (error) {
    console.error('Get ROI analysis data error:', error);
    throw error;
  }
}

export async function getSeasonalTrendsData() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('groups')
      ?.select('departure_date, revenue, costs, profit, margin')
      ?.eq('agency_id', user?.id)
      ?.not('departure_date', 'is', null)
      ?.order('departure_date', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get seasonal trends error:', error);
    throw error;
  }
}

// ============================================
// COST MANAGEMENT
// ============================================

export async function getCostEntries(groupId = null) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase?.from('cost_entries')?.select(`
        *,
        group:groups(name, type),
        approver:user_profiles!approved_by(name)
      `)?.eq('agency_id', user?.id)?.order('entry_date', { ascending: false });

    if (groupId) {
      query = query?.eq('group_id', groupId);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || [])?.map(entry => ({
      ...entry,
      groupName: entry?.group?.name || null,
      groupType: entry?.group?.type || null,
      approverName: entry?.approver?.name || null
    }));
  } catch (error) {
    console.error('Get cost entries error:', error);
    throw error;
  }
}

export async function createCostEntry(costEntryData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...costEntryData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('cost_entries')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create cost entry error:', error);
    throw error;
  }
}

export async function createBulkCostEntries(costEntriesData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeDataArray = costEntriesData?.map(entry => toSnakeCase({
      ...entry,
      agencyId: user?.id
    }));

    const { data, error } = await supabase?.from('cost_entries')?.insert(snakeDataArray)?.select();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create bulk cost entries error:', error);
    throw error;
  }
}

export async function updateCostEntry(id, costEntryData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase(costEntryData);

    const { data, error } = await supabase?.from('cost_entries')?.update(snakeData)?.eq('id', id)?.eq('agency_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update cost entry error:', error);
    throw error;
  }
}

export async function deleteCostEntry(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('cost_entries')?.delete()?.eq('id', id)?.eq('agency_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Delete cost entry error:', error);
    throw error;
  }
}

export async function getCostTemplates() {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('cost_templates')?.select('*')?.eq('agency_id', user?.id)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get cost templates error:', error);
    throw error;
  }
}

export async function createCostTemplate(templateData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...templateData,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('cost_templates')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create cost template error:', error);
    throw error;
  }
}

export async function deleteCostTemplate(id) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('cost_templates')?.delete()?.eq('id', id)?.eq('agency_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Delete cost template error:', error);
    throw error;
  }
}

export async function getGroupBudget(groupId) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('group_budgets')?.select('*')?.eq('group_id', groupId)?.eq('agency_id', user?.id)?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        return null;
      }
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get group budget error:', error);
    throw error;
  }
}

export async function createOrUpdateGroupBudget(groupId, budgetData) {
  try {
    const user = await getAuthUser();
    if (!user) throw new Error('Not authenticated');

    const snakeData = toSnakeCase({
      ...budgetData,
      groupId,
      agencyId: user?.id
    });

    const { data, error } = await supabase?.from('group_budgets')?.upsert(snakeData, { onConflict: 'group_id' })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create/update group budget error:', error);
    throw error;
  }
}